/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { MainHero } from '../blocks/MainHero';
import { AboutHero } from '../blocks/AboutHero';
import InfoCardGrid from '../blocks/InfoCardGrid';
import ProductsGallery from '../blocks/ProductsGallery';
import { AboutForm } from '../forms/AboutForm';
import { InputType } from '../../types';
import { TwoColTxtImg } from '../blocks';
import { CTATiles } from '../blocks/CTATiles';
import { Testimonials } from '../blocks';
import Timeline from '../blocks/Timeline';

export const AboutPage = () => {
  return (
    <>
      <AboutHero
        title={'<span>Made</span>  <span>for</span>  <span>the</span>  <span>way</span> <span><strong>you</strong>&nbsp;<strong>stream</strong></span>'}
        body={
          'With so many apps, channels and devices to juggle, streaming has gotten complicated. Xumo gets it — and <strong>Xumo gets you</strong>. That’s why we’re on a mission to <strong>make streaming easy</strong> so you can spend more time with the content you love. '
        }
        img="/static/images/refresh/2025/about-hero.jpg"
        mobileImg="/static/images/refresh/2025/about-hero.jpg"
        cta={{
          label: 'See Xumo in action',
          href: 'https://player.vimeo.com/progressive_redirect/playback/1143498534/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&log_user=0&signature=3d63670ce155af8bdfef831b4a5abc9eee6eb46b282d8ea8fcae6d71b1fa5016',
        }}
      />
      <InfoCardGrid
        stackedContent={[
          {
            heroTitle: 'Streaming that <strong>gets&nbsp;you</strong>',
            className: 'text-white mb-0 md:mb-12',
          },
          {
            bodyBase: 'A few principles guide our mission to make streaming easy — and they’re all made with you in mind.',
            className: 'text-white text-[24px] md:text-[32px] max-w-[350px] md:max-w-[500px] mx-auto lg:mx-0 lg:max-w-[900px]',
          },
        ]}
        infoCards={[
          {
            title: 'Watch <strong>more</strong>',
            body: 'Whatever you’re obsessed with is streaming on Xumo — from the big games and the latest movies to the reality show of the moment. We believe you should be able to <strong>find everything you want to watch, all in one place on Xumo</strong>. ',
          },
          {
            title: 'Watch <strong>easier</strong>',
            body: 'From the moment you jump in, Xumo helps you watch what you want faster with features that make streaming easy. We believe in an experience that helps you <strong>spend more time with the content you love — and less time looking for it</strong>.',
          },
          {
            title: 'Watch <strong>your&nbsp;way</strong>',
            body: 'No one else watches TV exactly like you. That’s why Xumo is innovating an experience that revolves around you. We believe in <strong>streaming that’s personalized to the way you watch</strong>. ',
          },
          {
            title: 'Watch <strong>smarter</strong>',
            body: 'With increasingly connected lives, Xumo strives to bring experiences together to be right at the heart of your home. We believe in unifying content and innovation on <strong>one intuitive platform that simplifies everything</strong>. ',
          },
        ]}
      />
      <ProductsGallery
        stackedContent={[
          {
            heroTitle: 'Our <strong>products</strong>',
            className: 'text-xumoDarkPlum',
          },
          {
            bodyBase:
              'We bring our principles to life through our products — a suite of streaming essentials that help you watch all the content you want, your way.',
            className: 'type-body max-w-[350px] md:max-w-[500px] mx-auto lg:mx-0 lg:max-w-[800px]',
          },
        ]}
        products={[
          {
            id: 'tv',
            image: {
              src: '/static/images/refresh/2025/product-gallery-1.jpg',
              alt: 'Xumo television mounted in a living room',
            },
            mobileImage: { src: '/static/images/refresh/2025/product-gallery-1-mobile.jpg', alt: 'Xumo television mounted in a living room' },
            logo: {
              src: '/static/images/refresh/2025/logos/Xumo_TV_Full_Color_SVG.svg',
              alt: 'Xumo TV',
            },
            heading: 'TV for the way <strong>you&nbsp;stream</strong>',
            body: 'Xumo TV is the smart TV platform that brings together everything you want to watch — like top apps, live TV and free content — in one simple experience that helps you spend more time streaming and less time looking.',
            button: { label: 'Meet Xumo TV', href: '/products/xumo-tv' },
            availability: {
              appStore: false,
              items: [
                {
                  store: 'amazon',
                  link: 'https://www.amazon.com/dp/B0FC34R88G/ref=va_live_carousel?pf_rd_r=XDEB75WKQ8CSQ5CVR9Q4&pf_rd_m=ATVPDKIKX0DER&pf_rd_t=liveDestination&pf_rd_i=video&linkCode=ilv&ascsubtag=VideoCreatorPortal*3Abd05590a15c44a7dbf10485258304f56&asc_',
                },
                {
                  store: 'bestBuy',
                  link: 'https://www.bestbuy.com/site/searchpage.jsp?st=xumo+tv&_dyncharset=UTF-8&_dynSessConf=&id=pcat17071&type=page&sc=Global&cp=1&nrp=&sp=&qp=&list=n&af=true&iht=y&usc=All+Categories&ks=960&keys=keys',
                },
                {
                  store: 'walmart',
                  link: 'https://www.walmart.com/ip/Element-43-4K-UHD-HDR-Frameless-Xumo-TV/5558619060?classType=REGULAR&athbdg=L1103&from=/search',
                },
              ],
            },
          },
          {
            id: 'streamBox',
            image: {
              src: '/static/images/refresh/2025/product-gallery-2.jpg',
              alt: 'People watching a Xumo television',
            },
            mobileImage: { src: '/static/images/refresh/2025/product-gallery-2-mobile.jpg', alt: 'People watching a Xumo television' },
            logo: {
              src: '/static/images/refresh/2025/logos/Xumo_Stream_Box_Full_Color_SVG.svg',
              alt: 'Xumo Stream Box',
            },
            heading: 'Everything you want, <strong>one&nbsp;easy&nbsp;device</strong>',
            body: 'Get more from your TV by plugging in Xumo Stream Box to bring together top apps and live TV in one easy experience.',
            button: { label: 'Meet Xumo Stream Box', href: '/products/xumo-stream-box' },
            availability: {
              appStore: false,
              items: [
                {
                  store: 'xfinity',
                  link: 'https://www.xfinity.com/learn/xumostreambox',
                },
                {
                  store: 'spectrum',
                  link: 'https://www.spectrum.com/cable-tv/streaming/xumo',
                },
                {
                  store: 'cox',
                  link: 'https://www.cox.com/residential/tv/streaming-devices.html',
                },
              ],
            },
          },
          {
            id: 'play',
            image: {
              src: '/static/images/refresh/2025/product-gallery-3.jpg',
              alt: 'Xumo Play mobile app',
            },
            mobileImage: { src: '/static/images/refresh/2025/product-gallery-3-mobile.jpg', alt: 'Xumo Play mobile app' },
            logo: {
              src: '/static/images/refresh/2025/logos/Xumo_Play_Full_Color_SVG.svg',
              alt: 'Xumo Play',
            },
            heading: '<strong>Stream free</strong> — at home&nbsp;or&nbsp;on&nbsp;the&nbsp;go',
            body: 'Tune in at home or on the go to Xumo Play, the free streaming app with 350+ channels and 70,000+ titles on demand — no subscription or login needed.',
            button: { label: 'Watch Now', href: 'https://play.xumo.com/live-guide/the-lucky-dog-channel' },
            availability: { appStore: true },
          },
        ]}
      />
      <Testimonials
        title={'People are talking'}
        testimonials={[
          {
            copy: `The voice remote makes it incredibly easy to search for whatever I want... It's completely intuitive out of the box.`,
            name: '— Xumo TV user',
          },
          {
            copy: `It is everything I expected, plus more. I can find everything I like to watch easily.`,
            name: '— Xumo Stream Box user',
          },
          {
            copy: `It's easy to navigate and I love the free content. It has everything I need in one place.`,
            name: '— Xumo Play user',
          },
        ]}
      />
      <Timeline
        content={[
          {
            heroTitle: 'Our <strong>story</strong>',
          },
          {
            bodyBase: 'You might be new to Xumo, but we’re not new to streaming — and we’ve been innovating since 2011.',
          },
        ]}
        carouselItems={[
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2011.jpg',
              alt: '',
            },
            date: '2011',
            copy: 'Xumo is founded',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2015.jpg',
              alt: '',
            },
            date: '2015',
            copy: 'Xumo’s free streaming app launches on VIZIO',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2021.jpg',
              alt: '',
            },
            date: '2021',
            copy: 'Xumo’s smart TV operating system launches on Hisense TVs',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2022.jpg',
              alt: '',
            },
            date: '2022',
            copy: '<p>Xumo’s easy-to-use streaming device, Xumo Stream Box, launches</p><p>Xumo’s free streaming app rebrands as Xumo Play</p>',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2023.jpg',
              alt: '',
            },
            date: '2023',
            copy: '<p>Xumo TV launches on Element and Pioneer TVs</p><p>Xumo Stream Box is available through Xfinity and Spectrum</p>',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2024.jpg',
              alt: '',
            },
            date: '2024',
            copy: '<p>Xumo Play launches in the UK on Sky devices</p><p>Xumo Stream Box is available through Cox and Mediacom</p>',
          },
          {
            img: {
              src: '/static/images/refresh/2025/timeline/2025.jpg',
              alt: '',
            },
            date: '2025',
            copy: 'Xumo TV launches on Westinghouse and Sharp TVs',
          },
        ]}
      />
      <TwoColTxtImg
        bg="bg-[#EFF3F5]"
        content={[
          { headingAlt: '<strong>We love streaming</strong><br/>as much as you' },
          {
            bodyBase:
              'We want it to be easier to watch our fave shows and movies, too. That’s why our team is always working on simple solutions that take the hassle out of&nbsp;streaming.',
          },
        ]}
        image={{
          src: '/static/images/refresh/2025/about-team.jpg',
          alt: 'tv graphic',
          width: 844,
          height: 628,
        }}
        flipped={false}
        rounded={true}
        textWidth={'lg:w-[90%] 2xl:w-[85%] 3xl:w-[95%]'}
        textPadding="0"
      />
      <CTATiles
        bgColor="[#EFF3F5]"
        contentLeft={[
          { headingAlt: '<strong>Join</strong> our team' },
          {
            bodyBase: 'Ready for your next step? Explore job openings at Xumo to be part of our mission to make streaming easy.',
          },
          {
            ctaRow: [
              {
                href: 'https://comcast.wd5.myworkdayjobs.com/Comcast_Careers/?q=xumo',
                label: 'Explore careers at Xumo',
                color: 'xumoAltBerryLight',
                primary: true,
                target: '_blank',
                fullalways: true,
              },
            ],
          },
        ]}
        contentRight={[
          { headingAlt: '<strong>Partner</strong> with us' },
          {
            bodyBase: 'Reach the next generation of streaming audiences by partnering with us to build FAST channels, advertise and more.',
          },
          {
            stackedCtaRow: [
              {
                href: 'https://www.xumo.com/enterprise',
                label: 'Explore Xumo Enterprise',
                color: 'xumoAltBerryLight',
                primary: true,
                fullalways: true,
              },
              {
                href: 'https://www.xumo.com/advertising',
                label: 'Explore Xumo Advertising',
                color: 'xumoAltBerryLight',
                primary: true,
                fullalways: true,
              },
            ],
          },
        ]}
      />
      <AboutForm
        heading="<h3>Level up <strong>your inbox</strong></h3>"
        body="<p>Sign up for emails from Xumo to keep up with all the latest shows and movies, new products and exclusive offers.</p>"
        formFields={[
          {
            type: InputType.Name,
            name: 'first_name',
            placeholder: 'First name',
          },
          {
            type: InputType.Name,
            name: 'last_name',
            placeholder: 'Last name',
          },
          {
            type: InputType.Email,
            name: 'email',
            placeholder: 'Email',
            width: 'full',
          },
        ]}
        CTA={{
          label: "Let's do this",
          href: ' ',
        }}
        backgroundColor="#ffffff"
        headingOne={true}
        styleOverrides={{
          form: 'mt-16 gap-2',
          formField: 'rounded-none outline-1 outline-[#C2C2C2] placeholder:text-black',
          hideLabel: true,
          CTA: { alignment: 'w-full md:w-fit self-center mt-6' },
          accentColor: 'xumoAltBerryLight',
          hoverColor: 'xumoAltBerryDark',
        }}
      />
    </>
  );
};
